import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AgxExternalConjunction,
  AgxTypography,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2';
import {
  ExternalConjunction,
  useInvalidateUseStaffGetExternalConjunction,
  useStaffGetExternalConjunction,
  useStaffUpdateExternalConjunction,
} from '@urbanx/agx.campaigns.client';
import Panel from 'components/panel/Panel';
import { CommissionMenu, FinanceMenu, MenuTitles } from 'constants/menu';
import { useSelectedCampaign } from 'hooks';
import { ContentLayout } from 'layout';
import { isDirtyEqual } from 'utils/compares';
import LoadingState from 'components/loadingState/LoadingState';
import { AgxToastState } from 'types/commonTypes';
import { AgxButton as AgxLegacyButton, AgxToast } from '@urbanx/agx-ui-components';

const ReferralPage = () => {
  const { campaign } = useSelectedCampaign();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const invalidateExternalConjunction = useInvalidateUseStaffGetExternalConjunction();

  const { data: externalConjunction, isPending } = useStaffGetExternalConjunction(
    campaign?.campaignId || '',
    !!campaign?.campaignId
  );

  const [currentValue, setCurrentValue] = useState<ExternalConjunction | undefined>(
    () => externalConjunction?.externalConjunction
  );

  useEffect(() => {
    setCurrentValue(externalConjunction?.externalConjunction);
    setIsLoaded(!isPending);
  }, [externalConjunction, isPending]);

  const { staffUpdateExternalConjunction, staffUpdateExternalConjunctionPending } =
    useStaffUpdateExternalConjunction({
      onSuccess: () => {},
      onError: () => {},
    });

  const isFormDirty = useMemo(() => {
    return !isDirtyEqual(currentValue, externalConjunction?.externalConjunction);
  }, [currentValue, externalConjunction]);

  const savingState = useMemo(() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING;
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED;
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED;
    if (isError) return SAVE_BUTTON_STATES.ERROR;
    return SAVE_BUTTON_STATES.UNSAVED;
  }, [isSaving, isError, isFormDirty, hasSaved]);

  useEffect(() => {
    if (isFormDirty) {
      setHasSaved(false);
    }
  }, [isFormDirty]);

  useEffect(() => {
    if (savingState === SAVE_BUTTON_STATES.SAVED) {
      updateToastState({
        color: 'success',
        message: 'Changes saved successfully',
        open: true,
      });
    }
  }, [savingState]);

  const onSave = useCallback(async () => {
    if (!campaign?.campaignId) return;

    setIsSaving(true);

    await staffUpdateExternalConjunction(
      {
        body: currentValue,
        campaignId: campaign.campaignId,
      },
      {
        onSuccess: async () => {
          await invalidateExternalConjunction(campaign.campaignId ?? '');
          setIsSaving(false);
          setHasSaved(true);
        },
        onError: () => {
          setIsError(true);
          setIsSaving(false);
        },
      }
    );
  }, [currentValue, externalConjunction, campaign?.campaignId, staffUpdateExternalConjunction]);

  if (!isLoaded) {
    return <LoadingState />;
  }

  return (
    <ContentLayout
      hasSecondaryMenu={false}
      secondaryActiveMenu={MenuTitles.COMMISSION}
      secondaryMenuOverride={FinanceMenu}
      isCampaigns
      tertiaryMenu={CommissionMenu}
      tertiaryActiveMenu={MenuTitles.COMMISSION_REFERRAL}
      tertiaryTitle="Commission"
      tertiaryActions={
        <AgxLegacyButton
          id="referralConjunctionSaveChanges"
          text="Save Changes"
          medium
          primary
          onClick={onSave}
          disabled={!isFormDirty || staffUpdateExternalConjunctionPending}
        />
      }
    >
      <AgxToast selector="#agxToast" toastState={toastState} />
      <Panel
        title={
          <AgxTypography id="panel" type="title" size="large">
            Referral / Conjunction
          </AgxTypography>
        }
        padding={{
          top: 'spacing-3xl',
          bottom: 'spacing-3xl',
          left: 'spacing-5xl',
          right: 'spacing-5xl',
        }}
      >
        <AgxExternalConjunction
          id="referral-conjunction-form"
          defaultValue={currentValue}
          onValueChange={setCurrentValue}
        />
      </Panel>
    </ContentLayout>
  );
};

export default ReferralPage;
